/**
 * Theme configuration and utilities for Worthly
 */

import confetti from 'canvas-confetti';
import { THEME_COLORS } from './constants';

/**
 * Get theme colors for Recharts (can't use CSS variables directly)
 * @returns {object} Theme color configuration
 */
export const getThemeColors = () => {
  const currentTheme = document.documentElement.getAttribute('data-theme') || 'pink';
  return THEME_COLORS[currentTheme] || THEME_COLORS.pink;
};

/**
 * Fire confetti animation with theme-appropriate colors
 */
export const fireConfetti = () => {
  const currentTheme = document.documentElement.getAttribute('data-theme') || 'pink';
  const themeConfettiColors = {
    pink: ['#e879a0', '#f472b6', '#fce7f3', '#fbcfe8', '#22c55e'],
    blue: ['#6366f1', '#818cf8', '#c7d2fe', '#e0e7ff', '#22c55e'],
    warm: ['#2D5A3D', '#3D8B5A', '#D8E8DC', '#F3E5D1', '#1B4027'],
    purple: ['#D6388A', '#8B2FE0', '#F1D8F7', '#D6D9FB', '#FBE3F3']
  };
  // Purple (admin-only) sits on top of Pink.
  const purple = document.documentElement.getAttribute('data-purple') === '1';
  confetti({
    particleCount: 80,
    spread: 70,
    origin: { y: 0.6 },
    colors: themeConfettiColors[purple ? 'purple' : currentTheme] || themeConfettiColors.pink
  });
};

/**
 * Get accent colors based on the current theme
 * @param {string} theme - The theme name ('pink', 'blue', 'sage', 'warm')
 * @returns {object} Object containing accent colors for the theme
 */
export const getAccentColors = (theme) => {
  const isWarm = theme === 'warm';
  const isBlue = theme === 'blue';

  // Primary color based on theme
  let primary;
  if (isWarm) primary = '#4d6b4d'; // forest green
  else if (isBlue) primary = '#6366f1'; // indigo
  else primary = '#e879a0'; // pink

  // Budget spent gradient colors
  let budgetSpentFrom, budgetSpentTo;
  if (isWarm) {
    // Keep warm theme as-is (uses theme gradient)
    budgetSpentFrom = null;
    budgetSpentTo = null;
  } else if (isBlue) {
    // Different shade of blue for blue theme
    budgetSpentFrom = '#818cf8';
    budgetSpentTo = '#6366f1';
  } else {
    // Pink theme: use same blue as Projected Net Worth box (#2b6cee → #1d5cd0)
    budgetSpentFrom = '#2b6cee';
    budgetSpentTo = '#1d5cd0';
  }

  return {
    primary,
    green: isWarm ? '#2D5A3D' : '#10b981',
    greenDark: isWarm ? '#1B4027' : '#059669',
    greenLight: isWarm ? '#D8E8DC' : '#d1fae5',
    blue: isWarm ? '#2D5A3D' : '#2b6cee',
    blueDark: isWarm ? '#1B4027' : '#1d5cd0',
    blueLight: isWarm ? '#D8E8DC' : '#dbeafe',
    budgetSpentFrom,
    budgetSpentTo
  };
};

/**
 * Color map for card colors - maps color names to Tailwind classes
 */
// softer card colours (patch 03): keep in step with BudgetTab's colorMap.
export const cardColorMap = {
  grey: { bg: 'bg-[#9FA2AC]', text: 'text-[#6B6B78]', bgLight: 'bg-[#E4E3E9]', border: 'border-[#E4E3E9]' },
  red: { bg: 'bg-[#E6827D]', text: 'text-[#C4443F]', bgLight: 'bg-[#F6C8C4]', border: 'border-[#F6C8C4]' },
  blue: { bg: 'bg-[#7C9FE6]', text: 'text-[#3F63C4]', bgLight: 'bg-[#C9D8F5]', border: 'border-[#C9D8F5]' },
  orange: { bg: 'bg-[#F0A06C]', text: 'text-[#B8683F]', bgLight: 'bg-[#FAD4B8]', border: 'border-[#FAD4B8]' },
  green: { bg: 'bg-[#6EC08F]', text: 'text-[#3F8A5F]', bgLight: 'bg-[#C8E8D3]', border: 'border-[#C8E8D3]' },
  pink: { bg: 'bg-[#E27FAB]', text: 'text-[#C23A72]', bgLight: 'bg-[#F7CCDE]', border: 'border-[#F7CCDE]' },
  purple: { bg: 'bg-[#A488E0]', text: 'text-[#7A5FB0]', bgLight: 'bg-[#DDD1F2]', border: 'border-[#DDD1F2]' }
};

/**
 * Get card color classes by card color name
 * @param {string} colorName - The color name ('grey', 'red', 'blue', 'orange', 'green')
 * @returns {object} Object containing Tailwind classes for the color
 */
export const getCardColorClasses = (colorName) => {
  return cardColorMap[colorName] || cardColorMap.grey;
};

/**
 * Default spending categories
 */
export const defaultCategories = [
  'Groceries', 'Gas', 'Entertainment', 'Shopping', 'Skincare & Makeup',
  'Restaurants', 'Takeout & Fast Food', 'Self Care', 'Nails', 'Hair',
  'Fitness', 'Hobby', 'Apple', 'Presto & Parking', 'Household',
  'Uber', 'Gifts', 'Car Maintenance', 'Travel', 'Miscellaneous'
];
